"use client";

import React, { useMemo } from "react";
import { Separator } from "@/components/ui/separator";
import Marquee from "react-fast-marquee";
import { useTranslation } from "react-i18next";
import { formatTrade } from "@/helpers/helperFunctions";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { brokersList } from "../connection/BrokerList";

// Reusable Stat Row Component
const Stat = ({ label, value }: { label: string; value: number | string }) => (
  <div className="flex gap-2 items-center not-italic">
    <div className="text-sm text-muted-foreground">{label} :</div>
    <div className="text-[--accent-secondary]">{value}</div>
  </div>
);

export const HeaderStats = ({ headerTrades }: { headerTrades: any }) => {
  const { t } = useTranslation("layout-page");
  const { details } = useAppDetails();

  const stats = useMemo(() => {
    const baseStats = [
      {
        label: t("Total strategies created "),
        value: formatTrade(headerTrades?.total_strategies),
      },
      {
        label: t("Total trades executed"),
        value: formatTrade(headerTrades?.total_trades),
      },
    ];

    if (details?.isWhiteLabled) {
      return [
        ...baseStats,
        {
          label: t("Live deployments"),
          value: headerTrades?.deployed_counts_per_broker?.[details?.id] ?? 0,
        },
        {
          label: t("Scanner deployments"),
          value: formatTrade(headerTrades?.scanners_deployed),
        },
      ];
    }

    return [
      ...baseStats,
      {
        label: t("Virtual deployments"),
        value: brokersList?.reduce((sum, broker) => {
          if (broker?.type === "virtual") {
            return (
              sum +
              (headerTrades?.deployed_counts_per_broker?.[broker?.id] ?? 0)
            );
          }
          return sum;
        }, 0),
      },
      {
        label: t("Live deployments"),
        value: brokersList?.reduce((sum, broker) => {
          if (broker?.type === "live") {
            return (
              sum +
              (headerTrades?.deployed_counts_per_broker?.[broker?.id] ?? 0)
            );
          }
          return sum;
        }, 0),
      },
      {
        label: t("Scanner deployments"),
        value: formatTrade(headerTrades?.scanners_deployed),
      },
    ];
  }, [details?.isWhiteLabled, headerTrades, t]);

  return (
    <>
      {/* Desktop layout */}
      <div className="xl:flex hidden flex-wrap">
        {stats.map((item, i) => (
          <React.Fragment key={i}>
            <Stat label={item.label} value={item.value} />
            {i !== stats.length - 1 && (
              <Separator orientation="vertical" className="mx-2" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile marquee */}
      <div className="xl:hidden flex max-w-screen w-full grow p-2">
        <Marquee className="max-w-screen">
          <ul className="relative z-10 flex flex-row gap-5 text-sm mr-3">
            {stats.map((item, i) => (
              <li key={i} className="flex gap-2 items-center min-w-[12rem]">
                <div className="text-sm text-muted-foreground">
                  {item.label} :
                </div>
                <div className="text-[--accent-primary]">{item.value}</div>
              </li>
            ))}
          </ul>
        </Marquee>
      </div>
    </>
  );
};

export default HeaderStats;
